import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Sparkles, ShieldCheck, Video, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { SITE, SERVICES } from "@/lib/site";

const CALENDLY_THEME = "primary_color=128091&text_color=1a1f2e&background_color=ffffff&hide_gdpr_banner=1";

const isCalendlyEvent = (e) =>
  e?.origin === "https://calendly.com" &&
  e?.data?.event &&
  typeof e.data.event === "string" &&
  e.data.event.indexOf("calendly.") === 0;

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    appointmentType: "telehealth",
  });
  const [booked, setBooked] = useState(false);
  const watchingRef = useRef(false);

  // Lazy-load Calendly script + CSS so the popup is ready when the user clicks Send.
  useEffect(() => {
    const sId = "calendly-widget-script";
    if (!document.getElementById(sId)) {
      const s = document.createElement("script");
      s.id = sId;
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.body.appendChild(s);
    }
    const cId = "calendly-widget-css";
    if (!document.getElementById(cId)) {
      const l = document.createElement("link");
      l.id = cId;
      l.rel = "stylesheet";
      l.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(l);
    }
  }, []);

  // Listen for Calendly booking confirmation → close popup, show success.
  useEffect(() => {
    const onMessage = (e) => {
      if (!isCalendlyEvent(e)) return;
      if (e.data.event === "calendly.event_scheduled") {
        try {
          window.Calendly?.closePopupWidget?.();
        } catch (_) {
          // no-op
        }
        setBooked(true);
        watchingRef.current = false;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }

    const contextLines = [
      form.phone ? `Phone: ${form.phone}` : null,
      form.service ? `Service: ${form.service}` : null,
      form.appointmentType ? `Visit type: ${form.appointmentType}` : null,
      "",
      form.message,
    ]
      .filter((l) => l !== null)
      .join("\n");

    // Convert phone to E.164 (e.g. "(312) 856-5029" -> "+13128565029").
    // Calendly's phone-type custom questions only accept international format.
    const digits = (form.phone || "").replace(/\D/g, "");
    let e164 = "";
    if (digits.length === 10) e164 = `+1${digits}`;
    else if (digits.length === 11 && digits.startsWith("1")) e164 = `+${digits}`;
    else if (digits.length > 0) e164 = `+${digits}`;

    const prefill = {
      name: form.name,
      email: form.email,
      // E.164 phone — populates Calendly's built-in phone fields:
      // - location (for "Phone call" events Calendly calls the invitee at this #)
      // - smsReminderNumber (SMS reminders)
      ...(e164 ? { location: e164, smsReminderNumber: e164 } : {}),
      customAnswers: {
        // a1 = "Please share anything that will help prepare for our meeting"
        a1: contextLines,
        // a2 = custom Phone Number question (must be E.164 to validate)
        ...(e164 ? { a2: e164 } : {}),
        // a3 = service (if you've added a service question to the event)
        a3: form.service || "",
      },
    };

    const url = `${SITE.calendly}?${CALENDLY_THEME}`;

    if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
      watchingRef.current = true;
      window.Calendly.initPopupWidget({ url, prefill });
    } else {
      const params = new URLSearchParams({ name: form.name, email: form.email }).toString();
      window.open(`${url}&${params}`, "_blank", "noopener,noreferrer");
      toast.info("Opening the scheduling page…");
    }
  };

  const reset = () => {
    setBooked(false);
    setForm({
      name: "",
      email: "",
      phone: "",
      service: "",
      message: "",
      appointmentType: "telehealth",
    });
  };

  // SUCCESS STATE — shown after Calendly confirms the booking
  if (booked) {
    return (
      <div
        data-testid="contact-success"
        className="relative rounded-3xl border border-primary/30 bg-card overflow-hidden p-8 md:p-12 text-center"
      >
        <div className="glow-orb -top-24 -right-10 w-[360px] h-[360px] bg-primary/30" />
        <div className="glow-orb -bottom-24 -left-10 w-[360px] h-[360px] bg-accent/25" />

        <div className="relative z-10 max-w-xl mx-auto">
          <span className="inline-grid place-items-center w-16 h-16 rounded-full bg-primary text-primary-foreground mb-5 animate-fade-up">
            <CheckCircle2 className="w-8 h-8" />
          </span>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3 animate-fade-up" style={{ animationDelay: "60ms" }}>
            Appointment booked.
          </h2>
          <p className="text-muted-foreground text-pretty leading-relaxed animate-fade-up" style={{ animationDelay: "120ms" }}>
            Thanks, {form.name.split(" ")[0] || "there"}. A confirmation and calendar invite has been emailed to{" "}
            <span className="font-medium text-foreground">{form.email || "your inbox"}</span>. Check your spam folder if you don&rsquo;t see it within a few minutes.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center animate-fade-up" style={{ animationDelay: "180ms" }}>
            <Button
              type="button"
              variant="outline"
              data-testid="contact-success-email"
              asChild
              className="rounded-full border-border h-11 px-6"
            >
              <a href={`mailto:${SITE.email}`}>
                <Mail className="w-4 h-4 mr-2" /> Email us
              </a>
            </Button>
            <Button
              type="button"
              data-testid="contact-success-reset"
              onClick={reset}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 px-6"
            >
              Book another appointment
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="relative rounded-3xl border border-border/80 bg-card/60 backdrop-blur-md p-6 md:p-10 overflow-hidden"
    >
      <div className="glow-orb -top-20 -right-20 w-72 h-72 bg-primary/30" />
      <div className="glow-orb -bottom-32 -left-20 w-72 h-72 bg-accent/20" />

      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">
            Request an appointment
          </span>
        </div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-2 text-balance">
          Tell us a little about yourself
        </h2>
        <p className="text-muted-foreground mb-8 max-w-md">
          Fill in your details, then pick a time. You&rsquo;ll receive a calendar invite by email.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="name">Full name *</Label>
            <Input
              id="name"
              data-testid="contact-input-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Jane Doe"
              required
              className="bg-background/60 border-border h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              data-testid="contact-input-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@example.com"
              required
              className="bg-background/60 border-border h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              data-testid="contact-input-phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="(312) 000-0000"
              className="bg-background/60 border-border h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="service">Service of interest</Label>
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger
                id="service"
                data-testid="contact-select-service"
                className="bg-background/60 border-border h-11"
              >
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.id} value={s.short}>
                    {s.short}
                  </SelectItem>
                ))}
                <SelectItem value="Other">Other / not sure</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2 space-y-2">
            <Label>Visit type</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "telehealth", label: "Telehealth", icon: Video },
                { id: "in-person", label: "In-person (Chicago)", icon: ShieldCheck },
                { id: "either", label: "Either", icon: Sparkles },
              ].map((o) => (
                <button
                  type="button"
                  key={o.id}
                  data-testid={`contact-visit-${o.id}`}
                  onClick={() => update("appointmentType", o.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm transition-all ${
                    form.appointmentType === o.id
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
                  }`}
                >
                  <o.icon className="w-3.5 h-3.5" /> {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 space-y-2">
            <Label htmlFor="message">How can we help? *</Label>
            <Textarea
              id="message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Briefly describe what you or your loved one is experiencing, and any preferred days/times."
              rows={5}
              required
              className="bg-background/60 border-border resize-none"
            />
          </div>
        </div>

        {/* Time selection notice */}
        <div className="mt-7 flex items-start gap-3 rounded-2xl border border-primary/25 bg-primary/5 px-4 py-3.5">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary text-primary-foreground shrink-0">
            <Calendar className="w-4 h-4" />
          </span>
          <div className="text-sm">
            <p className="font-medium text-foreground">Pick your time on the next step</p>
            <p className="text-muted-foreground">
              When you click <span className="font-semibold text-foreground">Send</span>, you&rsquo;ll choose a time slot. A confirmation and calendar invite will be emailed automatically.
            </p>
          </div>
        </div>

        <div className="mt-7 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 sm:justify-end">
          <Button
            type="submit"
            data-testid="contact-btn-submit"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 px-7 inline-flex items-center gap-2"
          >
            <Calendar className="w-4 h-4" /> Send & pick a time
          </Button>
        </div>
      </div>
    </form>
  );
}
